import { useQuery } from '@tanstack/react-query';
import { TreeSelect } from 'antd';
import { useMemo } from 'react';
import { buildOrgForest, fetchScopedOrgTree, sortTerritorialFirst, type OrgTreeNode } from '@/lib/orgTree';
import { queryKeys } from '@/lib/queryKeys';
import { NOT_AVAILABLE } from '@/lib/selectOptions';

/**
 * The org-unit picker, over the caller's own scope.
 *
 * `GET /org-units/tree` is already scoped server-side, so a PS nodal simply sees a
 * one-node tree. There is no client-side filtering to get wrong.
 *
 * DYNAMIC DEPTH: the tree is built by grouping on `parentId` and recursing until there
 * are no children left. Nothing here names COMMISSIONERATE, DIVISION or PS as levels.
 *
 * ⚠️ [amended WINGS.1] THAT PROMISE NOW HAS ONE STATED EXCEPTION, AND IT IS ABOUT ORDER
 * RATHER THAN STRUCTURE. `sortTerritorialFirst` names DIVISION, deliberately — "the
 * territorial ten come first" is not derivable from `parentId`. Nothing about the NESTING
 * names a level: the sort reorders SIBLINGS only, and `buildOrgForest` groups and recurses
 * exactly as it always did.
 */
export type OrgUnitPickerProps = {
  value?: string;
  onChange?: (value: string | undefined) => void;
  /** Ids that cannot be chosen — a node cannot be moved beneath itself. */
  disabledIds?: readonly string[];
  placeholder?: string;
  disabled?: boolean;
  allowClear?: boolean;
  /**
   * ⚠️ [amended C1c §5a] WHETHER THE TREE OPENS EXPANDED, AND IT DEFAULTS TO WHAT IT DID.
   *
   * The commissionerate is thirty-six divisions over seventy-eight stations, so expanding
   * everything puts a wall of about a hundred and fifteen rows in front of an operator who
   * wanted one. §5a asks for the OFFICER LIST's filter to open collapsed.
   *
   * ⚠️ IT IS A PROP DEFAULTING TO `true` RATHER THAN A CHANGE TO ALL SIX CONSUMERS, on §4a's
   * own instruction that an existing behaviour must not change under anyone. The other five —
   * the inbox division filter, the org unit drawer, the move-unit modal, the officer transfer
   * modal and the officer drawer — are untouched, and whether they should follow is a client
   * question rather than a silent decision taken here.
   *
   * ⚠️ AND A COLLAPSED TREE IS STILL FULLY REACHABLE. `showSearch` with
   * `treeNodeFilterProp="title"` matches at any depth, so typing three letters finds a station
   * without opening its division.
   */
  defaultExpandAll?: boolean;
};

type TreeNode = { value: string; title: string; disabled?: boolean; children: TreeNode[] };

const toTreeSelectData = (nodes: readonly OrgTreeNode[], disabledIds: readonly string[]): TreeNode[] =>
  nodes.map(({ unit, children }) => ({
    value: unit.id,
    title: `${unit.name} (${unit.shortCode})`,
    // A node cannot be moved beneath itself, and an inactive unit is not a valid posting.
    ...(disabledIds.includes(unit.id) || !unit.isActive ? { disabled: true } : {}),
    children: toTreeSelectData(children, disabledIds),
  }));

export const OrgUnitPicker = ({
  value,
  onChange,
  disabledIds = [],
  placeholder = 'Select an org unit',
  disabled,
  allowClear,
  defaultExpandAll = true,
}: OrgUnitPickerProps) => {
  /* ⚠️ [amended OTHERS-EXPAND] EVERY PAGE, NOT PAGE ONE — the tree outgrew 500 nodes. This key
     is shared with three other call sites and all four must use the same fetcher; read
     `fetchScopedOrgTree`'s header. */
  const { data, isFetching } = useQuery({
    queryKey: queryKeys.orgUnits.tree(),
    queryFn: ({ signal }) => fetchScopedOrgTree(signal),
  });

  /*
   * ⚠️ [amended WINGS.1] The territorial ten first. ONE edit reaches all six consumers of
   * this control — the Inbox and Officers filters, the create drawer's Parent unit, the
   * move modal, the officer transfer modal and the officer drawer — which is the whole
   * reason the picker is a component rather than six TreeSelects.
   */
  const treeData = useMemo(
    () => toTreeSelectData(buildOrgForest(sortTerritorialFirst(data?.items ?? [])), disabledIds),
    [data, disabledIds],
  );

  return (
    <TreeSelect
      value={value}
      onChange={(next: string | undefined) => onChange?.(next)}
      treeData={treeData}
      /*
       * ⚠️ [D4.5 §1b] A VALUE THIS TREE DOES NOT CONTAIN MUST NOT RENDER AS ITS ID.
       *
       * `GET /org-units/tree` is scoped SERVER-SIDE, so a unit outside the caller's own
       * subtree is absent from the data entirely — and `pages/Officers` drives this control
       * from a URL filter, so that id arrives in any link shared across scopes.
       * `rc-tree-select@5.27.0/lib/TreeSelect.js:300` then does `label ?? item.value` and
       * paints the ObjectId. See `lib/selectOptions.ts` for the general case.
       *
       * ⚠️ `treeTitleRender` REACHES THE SELECTION DISPLAY, NOT ONLY THE TREE ROWS, AND THAT
       * WAS MEASURED RATHER THAN ASSUMED — the name says "tree title", and the obvious
       * reading is that it cannot help with a value that has no node. It does: a bare
       * `TreeSelect` renders the raw id, and this renders the fallback.
       *
       * ⚠️ THE ALTERNATIVE WAS WORSE, and it also worked. Appending a disabled fallback NODE
       * to `treeData` resolves the label too, but it puts a unit nobody may pick at the ROOT
       * of the establishment, inside a control whose whole subject is the hierarchy. This
       * prop touches no data and fires only where nothing resolved.
       */
      treeTitleRender={(node) => node.title ?? NOT_AVAILABLE}
      treeDefaultExpandAll={defaultExpandAll}
      showSearch
      treeNodeFilterProp="title"
      placeholder={placeholder}
      disabled={disabled}
      allowClear={allowClear}
      loading={isFetching}
      className="w-full"
      data-testid="org-unit-picker"
    />
  );
};
